import { ref, watch, type Ref } from 'vue';

/**
 * Shared day/week(/hour) granularity toggle for the Active groups detail modals
 * (WeightDetailModal and PigTallyChart): a ref holding the active option, reset to the default
 * option whenever the open section changes. The component stays mounted between opens, so every
 * section shown - whether by opening or by prev/next navigation - starts back at the default
 * granularity.
 *
 * That reset IS the composable. A caller that does not want it wants a `ref`, not this.
 *
 * Chart-instance range resets (e.g. `chart.xAxis[0].setExtremes(...)`) are modal-specific and stay
 * local to each modal; call `resetGranularity()` from within that modal's own reset handler.
 */
export function useChartGranularity<T extends string>(options: T[], sectionId: Ref<number | null>, defaultOption: T) {
    const granularity = ref(defaultOption) as Ref<T>;

    watch(sectionId, (id) => {
        if (id !== null) granularity.value = defaultOption;
    });

    function resetGranularity(): void {
        granularity.value = defaultOption;
    }

    return {
        granularityOptions: options,
        granularity,
        resetGranularity,
    };
}
